/**
 * Kiosk Context - Manages kiosk state and cart
 * 
 * Phase 1A Refactor:
 * - Added orderType, tableNumber state
 * - Added tip, tipPercentage state
 * - Added promoCode, promoDiscount state
 * - Added loyaltyPhone (separate from customer phone)
 * - Added selectedItem for item detail modal
 * - Added upsellDismissed tracking
 * - Improved cart item structure with basePrice/totalPrice
 * - Added getCartSubtotal (the server prices the order: tax and total come from it)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUpdateHold } from '@/pwa/autoUpdate';
import axios from 'axios';
import { calculateTipFromPercentage } from '../utils/calculateTotals';

const API = process.env.REACT_APP_BACKEND_URL || '';

const KioskContext = createContext(null);

// This device's id (stable across reloads). The device token comes from pairing.
const getDeviceId = () => {
  let deviceId = localStorage.getItem('kiosk_device_id');
  if (!deviceId) {
    deviceId = `KIOSK-${window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`}`;
    localStorage.setItem('kiosk_device_id', deviceId);
  }
  return deviceId;
};

const TOKEN_KEY = 'kiosk_device_token';
// The order + card try in progress on this kiosk, so a reload resumes it (never a second charge).
const ACTIVE_KEY = 'kiosk_active_payment';
const CONFIG_REFRESH_MS = 60000;

const readActive = () => {
  try { return JSON.parse(localStorage.getItem(ACTIVE_KEY) || 'null'); } catch { return null; }
};
const writeActive = (value) => {
  try {
    if (value) localStorage.setItem(ACTIVE_KEY, JSON.stringify(value));
    else localStorage.removeItem(ACTIVE_KEY);
  } catch { /* storage unavailable: the server still holds the order */ }
};

export function KioskProvider({ children }) {
  // === DEVICE & CONFIG STATE ===
  const [deviceId] = useState(getDeviceId());
  const [deviceToken, setDeviceToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const paired = !!deviceToken;
  const [config, setConfig] = useState(null);
  const [menu, setMenu] = useState({ categories: [], featured: [] });
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('idle');
  
  // === ORDER TYPE STATE (NEW) ===
  const [orderType, setOrderType] = useState('pickup'); // 'pickup' | 'dine_in'
  const [tableNumber, setTableNumber] = useState('');
  
  // === CART STATE ===
  const [cart, setCart] = useState([]);
  const [order, setOrder] = useState(null);
  
  // === CUSTOMER INFO STATE ===
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // === LOYALTY STATE (ENHANCED) ===
  const [loyaltyPhone, setLoyaltyPhone] = useState('');
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  
  // === PROMO CODE STATE (NEW) ===
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null); // { type, value, amount, message }
  
  // === TIP STATE (NEW) ===
  const [tip, setTip] = useState(0);
  const [tipPercentage, setTipPercentage] = useState(null); // 0, 10, 15, 20, 'custom'
  
  // === ITEM SELECTION STATE (NEW) ===
  const [selectedItem, setSelectedItem] = useState(null); // For item detail modal
  
  // === UPSELL TRACKING (NEW) ===
  const [upsellDismissed, setUpsellDismissed] = useState({}); // { categoryId: true }
  
  // === PAYMENT IN PROGRESS (persisted) ===
  const [activePayment, setActivePaymentState] = useState(() => readActive()); // {order, payment_id, tip_cents, status}
  // a guest's cart or card payment is never interrupted by an app update (pwa/autoUpdate.js);
  // the idle reset empties the cart, then the update installs
  useUpdateHold('kiosk-order', cart.length > 0 || !!activePayment);
  const setActivePayment = useCallback((next) => {
    setActivePaymentState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      writeActive(value);
      return value;
    });
  }, []);
  // what the payment page is doing: form | busy (a card try is open) | result; the idle guard reads it
  const [paymentPhase, setPaymentPhase] = useState('form');

  // === IDLE (the IdleGuard in KioskApp watches every touch; pages may also report one) ===
  const lastTouchRef = useRef(Date.now());
  const resetIdleTimer = useCallback(() => { lastTouchRef.current = Date.now(); }, []);

  // Every kiosk call carries this device's token; a 401 means it was unpaired.
  const api = useCallback(async (method, path, data, extraHeaders) => {
    try {
      const res = await axios({ method, url: `${API}/api/kiosk/${deviceId}${path}`, data,
        headers: { Authorization: `Device ${deviceToken}`, ...(extraHeaders || {}) } });
      return res.data;
    } catch (error) {
      if (error?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setDeviceToken(null);
      }
      throw error;
    }
  }, [deviceId, deviceToken]);

  const initializeKiosk = useCallback(async () => {
    if (!deviceToken) { setLoading(false); return; }
    setLoading(true);
    try {
      const [configData, menuData, promoData] = await Promise.all([
        api('get', '/config'),
        api('get', '/menu'),
        api('get', '/promotions').catch(() => []),
      ]);
      setConfig(configData);
      setMenu(menuData);
      setPromotions(promoData);
    } catch (error) {
      console.error('Kiosk init error:', error);
    } finally {
      setLoading(false);
    }
  }, [api, deviceToken]);

  useEffect(() => { initializeKiosk(); }, [initializeKiosk]);

  // Card readiness, tip presets and the idle timeout can change: re-read the config.
  const refreshConfig = useCallback(async () => {
    if (!deviceToken) return null;
    try {
      const data = await api('get', '/config');
      setConfig(data);
      return data;
    } catch { return null; }
  }, [api, deviceToken]);

  useEffect(() => {
    if (!deviceToken) return undefined;
    const timer = setInterval(refreshConfig, CONFIG_REFRESH_MS);
    const onFocus = () => refreshConfig();
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); window.removeEventListener('online', onFocus); };
  }, [deviceToken, refreshConfig]);

  // Heartbeat so the owner's fleet page sees this kiosk online.
  useEffect(() => {
    if (!deviceToken) return undefined;
    const timer = setInterval(() => { api('post', '/heartbeat').catch(() => {}); }, 60000);
    return () => clearInterval(timer);
  }, [api, deviceToken]);

  const pairKiosk = async (code) => {
    const res = await axios.post(`${API}/api/kiosk/pair`, {
      code, device_uuid: deviceId, platform: 'PWA', device_name: navigator.userAgent.slice(0, 100),
    });
    localStorage.setItem(TOKEN_KEY, res.data.device_token);
    setDeviceToken(res.data.device_token);
    return res.data.kiosk;
  };

  /** A manager signed in on this screen: set the kiosk up with that one-time session. */
  const pairWithManagerToken = async (managerToken, kioskName, restaurantId) => {
    const headers = { Authorization: `Bearer ${managerToken}` };
    // an owner of several locations picked this one (checked server-side)
    if (restaurantId) headers['X-Merchant-ID'] = String(restaurantId);
    const res = await axios.post(`${API}/api/kiosk/pair-as-manager`, {
      kiosk_name: kioskName, device_uuid: deviceId, platform: 'PWA',
    }, { headers });
    localStorage.setItem(TOKEN_KEY, res.data.device_token);
    setDeviceToken(res.data.device_token);
    return res.data.kiosk;
  };

  const forgetPairing = () => {
    localStorage.removeItem(TOKEN_KEY);
    setDeviceToken(null);
    setConfig(null);
  };

  // Card readiness, tips and idle timeout, from the server (the server prices every order).
  const cardReady = !!config?.payment?.card_ready;
  const tipsEnabled = config?.payment?.tips_enabled !== false;
  const tipPresets = Array.isArray(config?.payment?.tip_presets) ? config.payment.tip_presets : [];
  const idleTimeoutSeconds = Math.max(15, Number(config?.settings?.idle_timeout) || 60);

  // === CART OPERATIONS ===
  
  /**
   * Add item to cart with modifiers
   * Enhanced to track basePrice and totalPrice separately
   * Also sets 'price' for backward compatibility
   */
  const addToCart = useCallback((item, modifiers = [], quantity = 1, instructions = '') => {
    const modifierTotal = modifiers.reduce((sum, m) => sum + (m.price || 0), 0);
    const totalPrice = item.price + modifierTotal;
    const cartItem = {
      id: `${item.id}-${Date.now()}`,
      menuItemId: item.id,
      name: item.name,
      basePrice: item.price,
      price: totalPrice, // Backward compatibility
      modifiers,
      modifierTotal,
      totalPrice,
      quantity,
      instructions,
      image_url: item.image_url
    };
    setCart(prev => [...prev, cartItem]);
    return cartItem;
  }, []);

  /**
   * Update cart item (for editing modifiers, quantity, etc.)
   */
  const updateCartItem = useCallback((cartItemId, updates) => {
    setCart(prev => prev.map(item => {
      if (item.id === cartItemId) {
        const updated = { ...item, ...updates };
        // Recalculate totalPrice if modifiers changed
        if (updates.modifiers) {
          updated.modifierTotal = updates.modifiers.reduce((sum, m) => sum + (m.price || 0), 0);
          updated.totalPrice = updated.basePrice + updated.modifierTotal;
        }
        return updated;
      }
      return item;
    }));
  }, []);

  const updateQuantity = useCallback((cartItemId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === cartItemId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  }, []);

  const removeFromCart = useCallback((cartItemId) => {
    setCart(prev => prev.filter(item => item.id !== cartItemId));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // === CART CALCULATIONS ===
  
  const getCartSubtotal = useCallback(() => {
    return cart.reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0);
  }, [cart]);

  const getCartCount = useCallback(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  // === TIP OPERATIONS (NEW) ===
  
  // `base` = the server's subtotal of the priced order (dollars)
  const setTipByPercentage = useCallback((percentage, base) => {
    setTipPercentage(percentage);
    if (percentage === 'custom') return; // Custom tip set separately
    const tipAmount = calculateTipFromPercentage(base ?? getCartSubtotal(), percentage);
    setTip(tipAmount);
  }, [getCartSubtotal]);

  const setCustomTip = useCallback((amount) => {
    setTipPercentage('custom');
    setTip(Math.max(0, amount));
  }, []);

  // === PROMO CODE OPERATIONS (NEW) ===
  
  const validatePromoCode = async (code) => {
    try {
      const data = await api('post', '/promo/validate', { code: code.toUpperCase(), items: orderLines() });
      if (data.valid) {
        setPromoCode(code.toUpperCase());
        setPromoDiscount({ amount: data.discount_amount, message: data.message });
        return { success: true, ...data };
      }
      return { success: false, message: data.message };
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.message || 'Invalid promo code';
      return { success: false, message: msg };
    }
  };

  const clearPromoCode = useCallback(() => {
    setPromoCode('');
    setPromoDiscount(null);
  }, []);

  // === LOYALTY OPERATIONS ===
  
  const lookupLoyalty = async (phone) => {
    try {
      const data = await api('post', '/loyalty/lookup', { phone });
      if (data.found) {
        setLoyaltyPhone(phone);
        setLoyaltyInfo(data);
        return { success: true, ...data };
      }
      return { success: false, message: 'No account found' };
    } catch (error) {
      console.error('Loyalty lookup failed:', error);
      return { success: false, message: 'Loyalty lookup failed' };
    }
  };

  const clearLoyalty = useCallback(() => {
    setLoyaltyPhone('');
    setLoyaltyInfo(null);
  }, []);

  // === ORDER OPERATIONS ===

  function orderLines() {
    return cart.map(item => ({
      menu_item_id: item.menuItemId,
      quantity: item.quantity,
      selected_modifiers: item.modifiers.map(m => m.id),
      special_instructions: item.instructions || null,
    }));
  }

  /** Create the order on the server (priced there); it reaches the kitchen once paid. */
  const placeOrder = async () => api('post', '/order', {
    items: orderLines(),
    order_type: orderType,
    customer_name: [customerName || 'Guest', orderType === 'dine_in' && tableNumber ? `Table ${tableNumber}` : null]
      .filter(Boolean).join(' - '),
    customer_phone: customerPhone || null,
    promo_code: promoCode || null,
    loyalty_phone: loyaltyPhone || null,
  });

  /** The customer left before paying: the order never reaches the kitchen. */
  const abandonOrder = async (orderId) => {
    try { await api('post', `/order/${orderId}/abandon`); } catch { /* already paid or in progress */ }
  };

  /**
   * Card on this kiosk's reader (Aster Gateway). Answers the PaymentView
   * (docs/CHECKOUT_API_2026-10-28.md §2); the screen polls it until final.
   * A reader the gateway reads offline answers 409 "Card reader unavailable".
   */
  const startCardPayment = async (orderId, tipCents = 0) =>
    api('post', `/order/${orderId}/card`, { tip_cents: Math.max(0, Math.round(tipCents)) });

  const getPayment = async (paymentId) => api('get', `/payments/${paymentId}`);

  const cancelPayment = async (paymentId) => api('post', `/payments/${paymentId}/cancel`);

  // === SESSION MANAGEMENT ===
  
  const resetSession = useCallback(() => {
    // The order in progress is forgotten here (the server keeps it: a late
    // approval still completes it, the sweep cancels one never paid).
    setActivePayment(null);
    setPaymentPhase('form');

    // Reset cart
    setCart([]);
    setOrder(null);
    
    // Reset order type
    setOrderType('pickup');
    setTableNumber('');
    
    // Reset customer info
    setCustomerName('');
    setCustomerPhone('');
    
    // Reset loyalty
    setLoyaltyPhone('');
    setLoyaltyInfo(null);
    
    // Reset promo
    setPromoCode('');
    setPromoDiscount(null);
    
    // Reset tip
    setTip(0);
    setTipPercentage(null);
    
    // Reset item selection
    setSelectedItem(null);
    
    // Reset upsell tracking
    setUpsellDismissed({});
    
    // Reset view
    setView('idle');
  }, [setActivePayment]);

  // === UPSELL TRACKING (NEW) ===
  
  const dismissUpsell = useCallback((categoryId) => {
    setUpsellDismissed(prev => ({ ...prev, [categoryId]: true }));
  }, []);

  const shouldShowUpsell = useCallback((categoryId) => {
    return !upsellDismissed[categoryId];
  }, [upsellDismissed]);

  return (
    <KioskContext.Provider value={{
      // Device & Config
      deviceId,
      kioskApi: api, // this device's own calls (Device token): the receipt doors
      paired,
      pairKiosk,
      pairWithManagerToken,
      forgetPairing,
      config,
      refreshConfig,
      cardReady,
      tipsEnabled,
      tipPresets,
      idleTimeoutSeconds,
      deviceToken,
      menu,
      promotions,
      loading,
      view,
      setView,
      
      // Order Type (NEW)
      orderType,
      setOrderType,
      tableNumber,
      setTableNumber,
      
      // Cart
      cart,
      order,
      setOrder,
      addToCart,
      updateCartItem,
      updateQuantity,
      removeFromCart,
      clearCart,
      
      // Cart Calculations
      getCartSubtotal,
      getCartCount,
      
      // Customer Info
      customerName,
      setCustomerName,
      customerPhone,
      setCustomerPhone,
      
      // Loyalty
      loyaltyPhone,
      setLoyaltyPhone,
      loyaltyInfo,
      setLoyaltyInfo,
      lookupLoyalty,
      clearLoyalty,
      
      // Promo Code (NEW)
      promoCode,
      promoDiscount,
      validatePromoCode,
      clearPromoCode,
      
      // Tip (NEW)
      tip,
      tipPercentage,
      setTip,
      setTipByPercentage,
      setCustomTip,
      
      // Item Selection (NEW)
      selectedItem,
      setSelectedItem,
      
      // Upsell (NEW)
      upsellDismissed,
      dismissUpsell,
      shouldShowUpsell,
      
      // Order Operations
      placeOrder,
      abandonOrder,
      startCardPayment,
      getPayment,
      cancelPayment,
      activePayment,
      setActivePayment,
      paymentPhase,
      setPaymentPhase,

      // Session
      resetSession,
      resetIdleTimer,
      lastTouchRef,
    }}>
      {children}
    </KioskContext.Provider>
  );
}

export const useKiosk = () => {
  const context = useContext(KioskContext);
  if (!context) {
    throw new Error('useKiosk must be used within a KioskProvider');
  }
  return context;
};
